<template>
    <div class="container-fluid pt-2">
        <div class="row">
            <div class="col-md-6 col-sm-12">
                <h5>Borrowed Stock</h5>
                <small class="text-muted">Stock borrowed from a supplier or shop to fulfil a sale — view and settle what's owed.</small>
            </div>
        </div>
        <hr />

        <div class="row py-1">
            <div class="col-md-4 col-sm-12 py-1">
                <select class="form-control" v-model="statusFilter" @change="fetchBorrowedStock">
                    <option value="">All statuses</option>
                    <option value="active">Active</option>
                    <option value="settled">Settled</option>
                </select>
            </div>
        </div>

        <div class="table-responsive">
            <table class="table table-hover">
                <thead>
                    <tr>
                        <th>Supplier/Shop</th>
                        <th>Product</th>
                        <th>Borrowed Qty</th>
                        <th>Sold Qty</th>
                        <th>Remaining Qty</th>
                        <th>Cost Price</th>
                        <th>Selling Price</th>
                        <th>Outstanding</th>
                        <th>Payment Status</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    <template v-for="row in rows">
                        <tr :key="row.id">
                            <td>
                                {{ row.supplier ? row.supplier.supplier : '—' }}
                                <span v-if="row.supplier" :class="['badge ml-1', row.supplier.type === 'shop' ? 'badge-info' : 'badge-secondary']">
                                    {{ row.supplier.type === 'shop' ? 'Shop' : 'Supplier' }}
                                </span>
                            </td>
                            <td>{{ row.product ? row.product.product : '—' }}</td>
                            <td>{{ row.quantity_borrowed }}</td>
                            <td>{{ row.quantity_sold }}</td>
                            <td>{{ row.remaining_quantity }}</td>
                            <td>
                                {{ convertCurrencyToShillings(row.cost_price) }}
                                <small v-if="row.product && priceDiffers(row.cost_price, row.product.wholesale_ppu)" class="d-block text-muted">
                                    Inventory: {{ convertCurrencyToShillings(row.product.wholesale_ppu) }}
                                </small>
                            </td>
                            <td>
                                {{ convertCurrencyToShillings(row.selling_price) }}
                                <small v-if="row.product && priceDiffers(row.selling_price, row.product.ppu)" class="d-block text-muted">
                                    Inventory: {{ convertCurrencyToShillings(row.product.ppu) }}
                                </small>
                            </td>
                            <td>{{ convertCurrencyToShillings(row.outstanding_amount) }}</td>
                            <td>
                                <span :class="['badge', paymentStatusClass(row)]">{{ paymentStatusLabel(row) }}</span>
                            </td>
                            <td class="text-nowrap">
                                <button class="btn btn-sm btn-link text-primary" @click="toggleHistory(row.id)">
                                    {{ expanded === row.id ? 'Hide history' : 'History' }}
                                </button>
                                <button v-if="row.outstanding_amount > 0" class="btn btn-sm btn-outline-primary" @click="openRecordPayment(row)">
                                    Record Payment
                                </button>
                                <button v-if="row.remaining_quantity > 0" class="btn btn-sm btn-outline-secondary" title="Stop tracking these as still owing - they're already in your stock count"
                                    @click="absorbRemaining(row)">
                                    Keep {{ row.remaining_quantity }} in Inventory
                                </button>
                            </td>
                        </tr>
                        <tr v-if="expanded === row.id" :key="row.id + '-history'">
                            <td colspan="10" class="bg-light">
                                <div v-if="row.purchase && row.purchase.supplier_payment && row.purchase.supplier_payment.length">
                                    <table class="table table-sm mb-0">
                                        <thead>
                                            <tr>
                                                <th>Date</th>
                                                <th>Amount</th>
                                                <th>Method</th>
                                                <th>Covered by</th>
                                                <th>Reference</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="payment in row.purchase.supplier_payment" :key="payment.id">
                                                <td>{{ formatDate(payment.payment_date) }}</td>
                                                <td>{{ convertCurrencyToShillings(payment.amount) }}</td>
                                                <td class="text-capitalize">{{ payment.payment_method || '—' }}</td>
                                                <td>{{ fundingSourceLabel(payment) }}</td>
                                                <td>{{ payment.reference || '—' }}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                                <small v-else class="text-muted">No settlement payments recorded yet.</small>
                                <div v-if="row.notes" class="mt-2"><small class="text-muted">Notes: {{ row.notes }}</small></div>
                            </td>
                        </tr>
                    </template>
                    <tr v-if="!loading && rows.length === 0">
                        <td colspan="10" class="text-center text-muted py-4">No borrowed stock records yet.</td>
                    </tr>
                </tbody>
            </table>
        </div>

        <b-modal ref="recordPaymentModal" hide-footer hide-header static>
            <supplier-payment-recording
                v-if="paymentTarget"
                :purchase-id="paymentTarget.purchase_id"
                :supplier-id="paymentTarget.supplier_id"
                :embedded="true"
                @saved="onPaymentSaved"
            ></supplier-payment-recording>
        </b-modal>
    </div>
</template>

<script>
import axios from 'axios';
import moment from 'moment';
import SupplierPaymentRecording from '../modal/SupplierPaymentRecording.vue';

export default {
    name: 'BorrowedStock',
    components: { SupplierPaymentRecording },
    data() {
        return {
            rows: [],
            loading: false,
            statusFilter: '',
            expanded: null,
            paymentTarget: null,
        };
    },
    mounted() {
        this.fetchBorrowedStock();
    },
    methods: {
        async fetchBorrowedStock() {
            this.loading = true;
            try {
                const { data } = await axios.get('/api/borrowed-stock', {
                    params: { status: this.statusFilter || undefined },
                });
                this.rows = data.data || [];
            } catch (e) {
                this.rows = [];
            } finally {
                this.loading = false;
            }
        },

        toggleHistory(id) {
            this.expanded = this.expanded === id ? null : id;
        },

        openRecordPayment(row) {
            this.paymentTarget = { purchase_id: row.purchase_id, supplier_id: row.supplier_id };
            this.$refs.recordPaymentModal.show();
        },

        onPaymentSaved() {
            this.$refs.recordPaymentModal.hide();
            this.paymentTarget = null;
            this.fetchBorrowedStock();
            this.$toasts && this.$toasts.push &&
                this.$toasts.push({ type: 'success', message: 'Payment recorded' });
        },

        async absorbRemaining(row) {
            try {
                await axios.patch(`/api/borrowed-stock/${row.id}/absorb-remaining`);
                this.fetchBorrowedStock();
                this.$toasts && this.$toasts.push &&
                    this.$toasts.push({ type: 'success', message: 'Marked as kept in inventory' });
            } catch (e) {
                this.$toasts && this.$toasts.push &&
                    this.$toasts.push({ type: 'error', message: 'Could not update this borrow' });
            }
        },

        priceDiffers(batchPrice, inventoryPrice) {
            return parseFloat(batchPrice) !== parseFloat(inventoryPrice);
        },

        paymentStatusLabel(row) {
            if (!row.purchase) return 'Unknown';
            if (row.outstanding_amount <= 0) return 'Paid';
            if (row.purchase.amount_paid > 0) return 'Partially Paid';
            return 'Unpaid';
        },

        paymentStatusClass(row) {
            const label = this.paymentStatusLabel(row);
            if (label === 'Paid') return 'badge-success';
            if (label === 'Partially Paid') return 'badge-warning';
            return 'badge-danger';
        },

        fundingSourceLabel(payment) {
            if (payment.funding_source === 'external') return payment.external_source_label || 'External';
            if (payment.funding_source === 'payment_method') return (payment.payment_type && payment.payment_type.payment_method) || 'Payment method';
            return 'Business cash';
        },

        formatDate(date) {
            return date ? moment(date).format('DD MMM YYYY') : '—';
        },
    },
};
</script>
